import * as def from '@/definitions'
import { ColorConstruct } from '@/utilityModels/colorConstruct'
import { ObjectLayer } from './objectLayer';
import { Vector2 } from '@/utilityModels/vector2';
import { ArrayHelper } from '@/helper/arrayHelper';

import * as Enumerable from "linq-es5";

// 図形ベースレイヤー
export class PatternBaseLayer extends ObjectLayer
{
    public get layerType() : string { return "patternBase"; }

    // 固定タイプ
    // 0: 縦横固定 *WEB/APPではこれを固定状態とする
    // 1; 縦固定 *WEB/APPではこれを非固定とする
    // 2: 横固定 *WEB/APPでは使用しないが、この数値のデータは非固定として扱う
    private _keepRatio : number = 0;
    public get keepRatio() : number
    {
        return this._keepRatio;
    }
    public set keepRatio(keepRatio : number)
    {
        this._keepRatio = keepRatio;
    }

    // 外線色
    private _strokeColor : ColorConstruct = new ColorConstruct();
    public get strokeColor() : ColorConstruct
    {
        return this._strokeColor;
    }
    public set strokeColor(strokeColor : ColorConstruct)
    {
        this._strokeColor = strokeColor;
    }

    // 線幅
    private _strokeWidth : number = 0;
    public get strokeWidth() : number
    {
        return this._strokeWidth;
    }
    public set strokeWidth(strokeWidth : number)
    {
        this._strokeWidth = strokeWidth;
    }

    //! FILE_VERSION == 6 で追加
    // 枠線なし
    private _noStroke : boolean = false;
    public get noStroke() : boolean
    {
        return this._noStroke;
    }
    public set noStroke(noStroke : boolean)
    {
        this._noStroke = noStroke;
    }

    // 塗りつぶしタイプ
    // 0: なし
    // 1: 単色
    // 2: グラデーション
    private _fillType : number = 0;
    public get fillType() : number
    {
        return this._fillType;
    }
    public set fillType(fillType : number)
    {
        this._fillType = fillType;
    }

    // 塗りつぶし色
    private _fillColor : ColorConstruct = new ColorConstruct();
    public get fillColor() : ColorConstruct
    {
        return this._fillColor;
    }
    public set fillColor(fillColor : ColorConstruct)
    {
        this._fillColor = fillColor;
    }

    // 不透明度
    private _opacity : number = 100;
    public get opacity() : number
    {
        return this._opacity;
    }
    public set opacity(opacity : number)
    {
        this._opacity = opacity;
    }

    // グラデーション種別
    // 0: 線形
    // 1: 円形
    private _gradationType : number = 0;
    public get gradationType() { return this._gradationType; }
    public set gradationType(gradationType) { this._gradationType = gradationType; }

    // 線形グラデーション角度(0~360)
    // 0で左 -> 右方向
    private _linearGradationAngle : number = 0;
    public get linearGradationAngle() { return this._linearGradationAngle; }
    public set linearGradationAngle(linearGradationAngle) { this._linearGradationAngle = linearGradationAngle; }

    // 円形グラデーション方向
    // 0: 中央
    // 1: 左上
    // 2: 右上
    // 3: 右下
    // 4: 左下
    private _radialGradationDirection : number = 0;
    public get radialGradationDirection() { return this._radialGradationDirection; }
    public set radialGradationDirection(radialGradationDirection) { this._radialGradationDirection = radialGradationDirection; }

    // グラデーション情報
    // position: 分岐点の位置(0~100)
    // color: 色
    private _gradationInfoArray : Array<{position : number, color : ColorConstruct}> = new Array();
    public get gradationInfoArray() { return this._gradationInfoArray; }
    public set gradationInfoArray(gradationInfoArray) { this._gradationInfoArray = gradationInfoArray; }
    public getGradation(index)
    {
        return this._gradationInfoArray[index];
    }
    public getGradationPointCount()
    {
        return this._gradationInfoArray.length;
    }
    public addGradation(position, color) : number
    {
        this._gradationInfoArray = ArrayHelper.add(this._gradationInfoArray, {position : position, color : color});
        // position順に並び替える
        this._gradationInfoArray = Enumerable.asEnumerable(this._gradationInfoArray).OrderBy(x => x.position).ToArray();
        // 追加したインデックスを返す
        var newIndex = 0;
        for(var i = 0; i < this._gradationInfoArray.length; ++i)
        {
            if(this._gradationInfoArray[i].position == position && this._gradationInfoArray[i].color.equals(color)) 
            { 
                newIndex = i;
                break; 
            }
        }

        return newIndex;
    }
    public removeGradation(index)
    {
        this._gradationInfoArray = ArrayHelper.removeAt(this._gradationInfoArray, index);
    }
    public changeGradationColor(index, color)
    {
        var clone = ArrayHelper.clone(this._gradationInfoArray);
        clone[index].color = color;
        this._gradationInfoArray = clone;
    }
    public changeGradationPosition(index, position) : number
    {
        var clone = ArrayHelper.clone(this._gradationInfoArray);
        clone[index].position = position;
        var color = clone[index].color;
        this._gradationInfoArray = clone;
        // position順に並び替える
        this._gradationInfoArray = Enumerable.asEnumerable(this._gradationInfoArray).OrderBy(x => x.position).ToArray();
        // 変更後のインデックスを返す
        var newIndex = 0;
        for(var i = 0; i < this._gradationInfoArray.length; ++i)
        {
            if(this._gradationInfoArray[i].position == position && this._gradationInfoArray[i].color.equals(color)) 
            { 
                newIndex = i;
                break; 
            }
        }

        return newIndex;
    }

    // 現在のグラデーション編集点を一時保持
    public selectedGradationPoint = 0;

    // 複製
    public clone()
    {
        var cloned = new PatternBaseLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : PatternBaseLayer)
    {
        layer.keepRatio = this._keepRatio;
        layer.strokeWidth = this._strokeWidth;
        layer.noStroke = this._noStroke;
        layer.strokeColor = this._strokeColor.clone();
        layer.fillType = this._fillType;
        layer.fillColor = this._fillColor.clone();
        layer.opacity = this._opacity;
        layer.gradationType = this._gradationType;
        layer.linearGradationAngle = this._linearGradationAngle;
        layer.radialGradationDirection = this._radialGradationDirection;
        for(var i = 0; i < this._gradationInfoArray.length; ++i)
        {
            layer._gradationInfoArray.push({ 
                position : this._gradationInfoArray[i].position, 
                color : this._gradationInfoArray[i].color.clone()}
            );
        }
        
        super.cloneParameter(layer);
    }

    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.keepRatio = this._keepRatio;
        jsonObject.strokeWidth = this._strokeWidth;
        jsonObject.noStroke = this._noStroke;
        jsonObject.strokeColor = this._strokeColor;
        jsonObject.fillType = this._fillType;
        jsonObject.fillColor = this._fillColor;
        jsonObject.opacity = this._opacity;
        jsonObject.gradationType = this._gradationType;
        jsonObject.linearGradationAngle = this._linearGradationAngle;
        jsonObject.radialGradationDirection = this._radialGradationDirection;
        jsonObject.gradationInfoArray = this._gradationInfoArray;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : PatternBaseLayer
    {
        var layer = new PatternBaseLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.keepRatio != null) { this._keepRatio = jsonData.keepRatio; }
        if(jsonData.strokeWidth != null) { this._strokeWidth = jsonData.strokeWidth; }
        if(jsonData.noStroke != null) { this._noStroke = jsonData.noStroke; }
        if(jsonData.strokeColor != null) { this._strokeColor = ColorConstruct.createFromJsonData(jsonData.strokeColor); }
        if(jsonData.fillType != null) { this._fillType = jsonData.fillType; }
        if(jsonData.fillColor != null) { this._fillColor = ColorConstruct.createFromJsonData(jsonData.fillColor); }
        if(jsonData.opacity != null) { this._opacity = jsonData.opacity; }
        if(jsonData.gradationType != null) { this._gradationType = jsonData.gradationType; }
        if(jsonData.linearGradationAngle != null) { this._linearGradationAngle = jsonData.linearGradationAngle; }
        if(jsonData.radialGradationDirection != null) { this._radialGradationDirection = jsonData.radialGradationDirection; }
        if(jsonData.gradationInfoArray != null)
        {
            for(var i = 0; i < jsonData.gradationInfoArray.length; ++i)
            {
                this._gradationInfoArray.push({
                    position : jsonData.gradationInfoArray[i].position, 
                    color : ColorConstruct.createFromJsonData(jsonData.gradationInfoArray[i].color)
                });
            }
        }

        super.loadParameterFromJson(jsonData);
    }
}